import type { VehicleReservation } from "../types/garage/vehicules";

/**
 * Deux prêts sur la MÊME voiture au même moment — à rendre visibles, pas à empiler.
 *
 * Le planning dessinait chaque réservation sur toute la hauteur de la ligne du véhicule :
 * un chevauchement (glissé sur une voiture occupée, ou reçu tel quel de ServiceBox)
 * cachait purement et simplement le prêt du dessous (Héric, 2026-09-30 : LEGROS sous
 * NEXITY sur la Logan). Le serveur refuse désormais d'en créer (409), mais ceux qui
 * existent doivent se voir.
 *
 * Même calcul d'occupation que le back (`reservation_span`, loaner_mirror.py) : bornes
 * qui se touchent = pas de conflit (rendue à 9 h, reprêtée à 9 h).
 */

export interface ClashLayout {
  /** Voie de la barre dans la ligne (0 = en haut). */
  lane: number;
  /** Nombre de voies de la ligne : la barre prend 1/lanes de la hauteur. */
  lanes: number;
  /** Chevauche au moins un autre prêt de la même voiture. */
  clash: boolean;
}

function absHours(date: string, hour: number): number {
  const [y, m, d] = date.split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 3_600_000 + hour;
}

export function reservationSpan(r: VehicleReservation): [number, number] | null {
  if (!r.startDate) return null;
  const end = r.endDate || r.startDate;
  if (r.startHour == null) return [absHours(r.startDate, 0), absHours(end, 24)];
  let endHour: number;
  if (r.startDate === end) {
    endHour =
      r.returnHour != null
        ? r.returnHour
        : r.durationHours != null
          ? r.startHour + r.durationHours
          : 24;
  } else {
    endHour = r.returnHour != null ? r.returnHour : 24;
  }
  return [absHours(r.startDate, r.startHour), absHours(end, endHour)];
}

/** Voies et conflits, par id de réservation, pour les réservations d'UNE voiture. */
export function layoutVehicleReservations(
  reservations: VehicleReservation[],
): Record<string, ClashLayout> {
  const items = reservations
    .map((r) => ({ r, span: reservationSpan(r) }))
    .filter((x): x is { r: VehicleReservation; span: [number, number] } => x.span !== null)
    .sort((a, b) => a.span[0] - b.span[0] || a.span[1] - b.span[1]);

  const out: Record<string, ClashLayout> = {};
  const laneEnds: number[] = [];
  for (const { r, span } of items) {
    let lane = laneEnds.findIndex((end) => end <= span[0]);
    if (lane < 0) {
      lane = laneEnds.length;
      laneEnds.push(span[1]);
    } else {
      laneEnds[lane] = span[1];
    }
    out[r.id] = { lane, lanes: 1, clash: false };
  }
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (items[j].span[0] >= items[i].span[1]) break;
      out[items[i].r.id].clash = true;
      out[items[j].r.id].clash = true;
    }
  }
  const lanes = Math.max(1, laneEnds.length);
  for (const id of Object.keys(out)) out[id].lanes = lanes;
  return out;
}
